import {
    ComponentNodeDepsCond,
    IComponentNodeDepsGroup,
    IComponentNodeDepsRule,
} from 'app/app/editor/tabs/settings/deps/index';
import { alpha, Box, Button, IconButton, Input, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import React from 'react';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { useAppBuilderContext } from 'app/app/editor/context';
import { FormComponentValue } from 'components/common/form/layout/control';
import IconMi from 'components/common/icon/IconMi';

function DepsRule({
    rule,
    onChange,
}: {
    rule: IComponentNodeDepsRule;
    onChange: (newRule: IComponentNodeDepsRule) => void;
}) {
    const ctx = useAppBuilderContext();
    const stateValues: Array<FormComponentValue> = [{ value: '', label: '--' }];
    for (const [key, value] of Object.entries(ctx.state)) {
        for (const [k, v] of Object.entries(value as object)) {
            const p = `${key}.${k}`;
            stateValues.push({ value: p, label: p });
        }
    }
    return (
        <Stack spacing={0.5}>
            <Box>Source</Box>
            <Box>
                <ReactSelect
                    data={[
                        { value: 'state', label: 'state' },
                        { value: 'user', label: 'user' },
                    ]}
                    value={rule.sourceType}
                    update={(value) => {
                        onChange({ ...rule, sourceType: value });
                    }}
                />
            </Box>
            {rule.sourceType == 'state' ? (
                <Box>
                    <ReactSelect
                        data={stateValues}
                        value={rule.sourceValue}
                        update={(value) => {
                            onChange({ ...rule, sourceValue: value });
                        }}
                    />
                </Box>
            ) : (
                <Box>
                    <ReactSelect
                        data={[{ label: 'type', value: 'type' }]}
                        value={rule.sourceValue}
                        update={(value) => {
                            onChange({ ...rule, sourceValue: value });
                        }}
                    />
                </Box>
            )}
            <Box>
                <ReactSelect
                    data={ComponentNodeDepsCond.map((v) => ({ label: v, value: v }))}
                    value={rule.condition}
                    update={(value) => {
                        onChange({ ...rule, condition: value });
                    }}
                />
            </Box>
            <Box>
                <Input
                    fullWidth
                    value={rule.compareParam1}
                    onChange={(e) => {
                        // setCompareParam1
                        onChange({ ...rule, compareParam1: e.target.value });
                    }}
                />
            </Box>
        </Stack>
    );
}

export default function DepsGroup({
    deps,
    onChange,
}: {
    deps: IComponentNodeDepsGroup;
    onChange: (newDeps: IComponentNodeDepsGroup) => void;
}) {
    const handleAddRule = () => {
        deps.nodes.push({
            sourceType: 'state',
            sourceValue: '',
            condition: 'exactly matches',
            compareParam1: '',
        });
        onChange(deps);
    };

    const handleAddGroup = () => {
        deps.nodes.push({
            condition: 'AND',
            nodes: [],
        });
        onChange(deps);
    };

    return (
        <Stack
            sx={{ p: 1, border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.16)}`, borderRadius: 1 }}
        >
            <Stack direction={'row'} justifyContent={'space-between'}>
                <Box sx={{ flexShrink: 0 }}>
                    <ToggleButtonGroup
                        value={deps.condition}
                        exclusive
                        onChange={(event, value) => {
                            if (value !== null) {
                                onChange({ ...deps, condition: value as 'AND' | 'OR' });
                            }
                        }}
                    >
                        <ToggleButton value="AND">AND</ToggleButton>
                        <ToggleButton value="OR">OR</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{ mt: -1 }}>
                    <Button
                        variant={'light'}
                        onClick={() => handleAddRule()}
                        startIcon={<IconMi icon="new" />}
                        sx={{ ml: 1, mt: 1 }}
                    >
                        Rule
                    </Button>
                    <Button
                        variant={'light'}
                        onClick={() => handleAddGroup()}
                        startIcon={<IconMi icon="new" />}
                        sx={{ ml: 1, mt: 1 }}
                    >
                        Group
                    </Button>
                </Box>
            </Stack>
            <Box>
                {deps.nodes.map((node, index) => {
                    return (
                        <Stack direction={'row'} key={index} alignItems={'center'} sx={{ mt: 1 }}>
                            <Box flexGrow={1}>
                                {typeof (node as IComponentNodeDepsGroup)?.nodes == 'object' ? (
                                    <DepsGroup
                                        deps={node as IComponentNodeDepsGroup}
                                        onChange={(newDeps) => {
                                            deps.nodes[index] = newDeps;
                                            onChange(deps);
                                        }}
                                    />
                                ) : (
                                    <DepsRule
                                        rule={node as IComponentNodeDepsRule}
                                        onChange={(newRule) => {
                                            deps.nodes[index] = newRule;
                                            onChange(deps);
                                        }}
                                    />
                                )}
                            </Box>
                            <Box flexShrink={0}>
                                <IconButton
                                    onClick={() => {
                                        deps.nodes.splice(index, 1);
                                        onChange(deps);
                                    }}
                                    sx={{ p: 0.5 }}
                                >
                                    <IconMi icon="delete" fontSize="16" />
                                </IconButton>
                            </Box>
                        </Stack>
                    );
                })}
            </Box>
        </Stack>
    );
}
